import {
  Alert,
  Button,
  ButtonGroup,
  closeCurrentDialog,
  DataStatus,
  showNotification,
} from 'platform/components';
import {Hide, Show, Text, VStack} from 'platform/foundation';

import {isNotEmpty} from 'ramda';

import {
  useDeactivateBillingInformationMutation,
  useGetBillingInformationUsagesQuery,
} from '@dms/api/core';
import {
  useGetEmployeeCostCentersListQuery,
  useGetEmployeeWorkTeamsListQuery,
} from '@dms/api/customer';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {ApiError, Nullish} from 'shared/web';

type DeactivateBillingInformationDialogContentProps = {
  billingInformationId: string;
  billingName: string;
  onSuccess: () => void;
};

type EmployeeManagementRecord = {
  description?: string | Nullish;
  isActive?: boolean | Nullish;
};

/**
 * Only active records block, in line with the active-branch rule of the backend usages check;
 * a record without isActive counts as active. Records without a description are skipped.
 */
const getBlockingRecordNames = (records: (EmployeeManagementRecord | Nullish)[] | Nullish) =>
  (records ?? [])
    .filter((record) => record?.isActive !== false)
    .map((record) => record?.description ?? '')
    .filter(isNotEmpty);

export function DeactivateBillingInformationDialogContent(
  props: DeactivateBillingInformationDialogContentProps
) {
  const {
    data: usages,
    isLoading: isUsagesLoading,
    isError: isUsagesError,
    refetch: refetchUsages,
  } = useGetBillingInformationUsagesQuery(
    {billingInformationId: props.billingInformationId},
    {refetchOnMountOrArgChange: true}
  );

  /**
   * Employee cost centres and work teams live in Metada, so the backend cannot reject the
   * deactivation on their behalf. These two groups are a frontend-side check only.
   */
  const {
    data: costCenters,
    isLoading: isCostCentersLoading,
    isError: isCostCentersError,
  } = useGetEmployeeCostCentersListQuery(
    {company: props.billingInformationId},
    {refetchOnMountOrArgChange: true}
  );

  const {
    data: workTeams,
    isLoading: isWorkTeamsLoading,
    isError: isWorkTeamsError,
  } = useGetEmployeeWorkTeamsListQuery(
    {companyId: props.billingInformationId},
    {refetchOnMountOrArgChange: true}
  );

  const [deactivateBillingInformation, {isLoading: isDeactivating}] =
    useDeactivateBillingInformationMutation();

  const branchNames = usages?.branches.map((branch) => branch.name) ?? [];
  const vehicleWarehouseNames =
    usages?.vehicleWarehouses.map((vehicleWarehouse) => vehicleWarehouse.name) ?? [];
  const costCenterNames = getBlockingRecordNames(costCenters?.costCenters);
  const workTeamNames = getBlockingRecordNames(workTeams?.workTeams);
  const isBlocked =
    isNotEmpty(branchNames) ||
    isNotEmpty(vehicleWarehouseNames) ||
    isNotEmpty(costCenterNames) ||
    isNotEmpty(workTeamNames);
  const isLoading = isUsagesLoading || isCostCentersLoading || isWorkTeamsLoading;

  /**
   * An unreachable Metada must not make a legitimate deactivation impossible: the backend does not
   * enforce the employee-management groups at all, so a failed check only shows a note and neither
   * blocks the deactivation nor fails the dialog.
   */
  const isEmployeeManagementCheckFailed = isCostCentersError || isWorkTeamsError;

  const handleConfirm = () =>
    deactivateBillingInformation({billingInformationId: props.billingInformationId})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.successfullyUpdated'));
        closeCurrentDialog();
        props.onSuccess();
      })
      .catch((error: ApiError) => handleApiError(error, {callback: () => refetchUsages()}));

  return (
    <VStack spacing={4}>
      <DataStatus isLoading={isLoading} isError={isUsagesError} minHeight={30}>
        <Show when={isBlocked}>
          <VStack spacing={4}>
            <Text>{i18n.t('page.settings.labels.deactivateBillingInformationBlocked')}</Text>
            <Show when={isNotEmpty(branchNames)}>
              <VStack spacing={1}>
                <Text>
                  {i18n.t('page.settings.labels.deactivateBillingInformationBlockedBranches')}
                </Text>
                <Text size="small" color="secondary">
                  {i18n.t('entity.branch.labels.branches')}
                </Text>
                <Text>{branchNames.join(', ')}</Text>
              </VStack>
            </Show>
            <Show when={isNotEmpty(vehicleWarehouseNames)}>
              <VStack spacing={1}>
                <Text>
                  {i18n.t(
                    'page.settings.labels.deactivateBillingInformationBlockedVehicleWarehouses'
                  )}
                </Text>
                <Text size="small" color="secondary">
                  {i18n.t('page.vehicleWarehouseManagement.labels.vehicleWarehouses')}
                </Text>
                <Text>{vehicleWarehouseNames.join(', ')}</Text>
              </VStack>
            </Show>
            <Show when={isNotEmpty(costCenterNames)}>
              <VStack spacing={1}>
                <Text>
                  {i18n.t('page.settings.labels.deactivateBillingInformationBlockedCostCenters')}
                </Text>
                <Text size="small" color="secondary">
                  {i18n.t('entity.employeeCostCenters.labels.title')}
                </Text>
                <Text>{costCenterNames.join(', ')}</Text>
              </VStack>
            </Show>
            <Show when={isNotEmpty(workTeamNames)}>
              <VStack spacing={1}>
                <Text>
                  {i18n.t('page.settings.labels.deactivateBillingInformationBlockedWorkTeams')}
                </Text>
                <Text size="small" color="secondary">
                  {i18n.t('entity.employeeWorkTeams.labels.title')}
                </Text>
                <Text>{workTeamNames.join(', ')}</Text>
              </VStack>
            </Show>
          </VStack>
        </Show>
        <Hide when={isBlocked}>
          <Text>
            {i18n.t('page.settings.labels.deactivateBillingInformationDescription', {
              name: props.billingName,
            })}
          </Text>
        </Hide>
      </DataStatus>
      <Show when={isEmployeeManagementCheckFailed}>
        <Alert
          variant="warning"
          message={i18n.t(
            'page.settings.labels.deactivateBillingInformationEmployeeManagementCheckFailed'
          )}
          data-testid="deactivateBillingInformationDialogEmployeeManagementCheckFailed"
        />
      </Show>
      <ButtonGroup align="right">
        <Button
          data-testid="deactivateBillingInformationDialogCancel"
          variant="secondary"
          onClick={closeCurrentDialog}
          title={i18n.t('general.actions.close')}
        />
        <Button
          data-testid="deactivateBillingInformationDialogConfirm"
          variant="danger"
          onClick={handleConfirm}
          isLoading={isDeactivating}
          isDisabled={isBlocked || isLoading || isUsagesError}
          title={i18n.t('general.actions.deactivate')}
        />
      </ButtonGroup>
    </VStack>
  );
}
